'use client';

import * as React from 'react';
import { NavLink } from 'react-router-dom';

import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
} from './sidebar';

export interface NavItemProps {
  /** Route path, passed to react-router-dom's `NavLink`. */
  to: string;
  label: string;
  icon?: React.ComponentType<{ className?: string }>;
  /** Exact-match routing (`NavLink`'s `end`). */
  end?: boolean;
  /** Renders the item disabled with a "Coming soon" tooltip instead of a link. */
  disabled?: boolean;
  /** Tooltip shown when the sidebar is collapsed to icon width, or (with `disabled`) always. */
  tooltip?: string;
}

/**
 * Single top-level nav link: `SidebarMenuItem` + `NavLink` + `SidebarMenuButton`,
 * the pattern every one of the six app sidebars repeated by hand. Sections with
 * sub-items (e.g. admin's collapsible `NavSection`) stay app-specific — they
 * compose `SidebarMenuItem`/`Collapsible` directly rather than through `NavItem`.
 */
export function NavItem({ to, label, icon: Icon, end, disabled, tooltip }: NavItemProps) {
  if (disabled) {
    return (
      <SidebarMenuItem>
        <SidebarMenuButton disabled tooltip={tooltip ?? 'Coming soon'}>
          {Icon && <Icon className="size-4" />}
          <span>{label}</span>
        </SidebarMenuButton>
      </SidebarMenuItem>
    );
  }

  return (
    <SidebarMenuItem>
      <NavLink to={to} end={end}>
        {({ isActive }) => (
          <SidebarMenuButton isActive={isActive} tooltip={tooltip}>
            {Icon && <Icon className="size-4" />}
            <span>{label}</span>
          </SidebarMenuButton>
        )}
      </NavLink>
    </SidebarMenuItem>
  );
}

export interface L3AppSidebarProps
  extends Omit<React.ComponentProps<typeof Sidebar>, 'children'> {
  /**
   * Rendered inside `SidebarHeader`, pinned above the scrollable content —
   * the OS app keeps its `<AppSwitcher>` here so it never scrolls out of
   * view. Mutually independent from `appSwitcher`.
   */
  header?: React.ReactNode;
  /**
   * Rendered in its own `SidebarGroup` above the primary nav — typically
   * `<AppSwitcher>`. Omit for a sidebar with no switcher (e.g. opps).
   */
  appSwitcher?: React.ReactNode;
  /**
   * Primary nav items, rendered as a single `SidebarMenu` inside one
   * `SidebarGroup`. Omit and build nav via `children` for custom composition
   * (grouped sections, collapsible sub-items — see admin's sidebar).
   */
  navItems?: NavItemProps[];
  /**
   * App-specific sidebar content rendered after `navItems` (by-client lists,
   * admin-only groups, etc). Each app keeps this content; the scaffold only
   * provides the outer chrome.
   */
  children?: React.ReactNode;
  /**
   * Rendered in the footer above `account` — e.g. the `ThemeToggle` +
   * `NotificationsBell` row that opps/timeoff/timesheet show.
   */
  footerExtra?: React.ReactNode;
  /**
   * Account widget slot. Every sidebar has one; this package takes it as a
   * `ReactNode` so it stays Clerk-agnostic (the concrete `AccountSection` is
   * built per-app, wrapping `@loop3/ui`'s `AccountWidget`).
   */
  account: React.ReactNode;
}

/**
 * Shared sidebar scaffold for L3 "sidebar apps" (admin, app/OS, meetings,
 * opps, timeoff, timesheet). Mirrors `AppShell`'s pattern: this component
 * owns layout (Sidebar → SidebarContent → groups → SidebarFooter), each app
 * supplies its own nav data, extra sections, and account widget.
 *
 * `variant="floating"` is hardcoded — all six sidebars use it. `collapsible`
 * is NOT defaulted here: only admin uses `"icon"`, the other five use
 * `"offcanvas"` (`Sidebar`'s own default), so callers pass it explicitly.
 */
export function L3AppSidebar({
  header,
  appSwitcher,
  navItems,
  children,
  footerExtra,
  account,
  ...sidebarProps
}: L3AppSidebarProps) {
  return (
    <Sidebar variant="floating" {...sidebarProps}>
      {header && <SidebarHeader>{header}</SidebarHeader>}
      <SidebarContent>
        {appSwitcher && <SidebarGroup>{appSwitcher}</SidebarGroup>}
        {navItems && navItems.length > 0 && (
          <SidebarGroup>
            <SidebarGroupContent>
              <SidebarMenu>
                {navItems.map((item) => (
                  <NavItem key={item.to} {...item} />
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        )}
        {children}
      </SidebarContent>
      <SidebarFooter>
        {footerExtra}
        {account}
      </SidebarFooter>
    </Sidebar>
  );
}
